import React, { useId } from 'react';

/** Default doc mark — gradient page. 📄 is treated as “no custom icon”. */
export const DEFAULT_DOC_EMOJI = '📄';

export const isDefaultDocIcon = (icon) => {
  if (icon == null || icon === '') return true;
  if (icon === DEFAULT_DOC_EMOJI) return true;
  if (typeof icon === 'object' && icon.type === 'emoji') {
    return !icon.value || icon.value === DEFAULT_DOC_EMOJI;
  }
  return false;
};

export const DocIcon = ({ size = 14, className }) => {
  const uid = useId().replace(/:/g, '');
  const fillId = `doc-ai-fill-${uid}`;
  const backId = `doc-ai-back-${uid}`;
  const foldId = `doc-ai-fold-${uid}`;
  const sheenId = `doc-ai-sheen-${uid}`;
  const clipId = `doc-ai-clip-${uid}`;

  const l = 6.15;
  const t = 3.3;
  const r = 18.8;
  const b = 20.85;
  const rx = 1.35;
  const foldX = 13.35;
  const foldY = 8.7;
  const foldRx = 1.05;

  const page = [
    `M${l + rx} ${t}`,
    `H${foldX}`,
    `L${r} ${foldY}`,
    `V${b - rx}`,
    `A${rx} ${rx} 0 0 1 ${r - rx} ${b}`,
    `H${l + rx}`,
    `A${rx} ${rx} 0 0 1 ${l} ${b - rx}`,
    `V${t + rx}`,
    `A${rx} ${rx} 0 0 1 ${l + rx} ${t}`,
    'Z',
  ].join(' ');

  const fold = [
    `M${foldX} ${t}`,
    `L${r} ${foldY}`,
    `H${foldX + foldRx}`,
    `A${foldRx} ${foldRx} 0 0 1 ${foldX} ${foldY - foldRx}`,
    'Z',
  ].join(' ');

  const showLines = size >= 18;
  const hair = 24 / size;
  const lineX = 8.45;
  const lineW = 7.3;
  const lineY0 = 11.35;
  const lineGap = 2.2;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden
      overflow="visible"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        <linearGradient id={backId} x1="8" y1="10" x2="20" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1e40af" />
          <stop offset="100%" stopColor="#312e81" />
        </linearGradient>
        <linearGradient id={fillId} x1="6" y1="3" x2="19" y2="21" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="36%" stopColor="#3b82f6" />
          <stop offset="72%" stopColor="#2563eb" />
          <stop offset="100%" stopColor="#3730a3" />
        </linearGradient>
        <linearGradient id={foldId} x1={foldX} y1={t} x2={r} y2={foldY} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="55%" stopColor="#2563eb" />
          <stop offset="100%" stopColor="#1e3a8a" />
        </linearGradient>
        <radialGradient id={sheenId} cx="24%" cy="14%" r="46%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <clipPath id={clipId}>
          <path d={page} />
        </clipPath>
      </defs>

      <path
        d="M7.7 10.7h10.35A1.15 1.15 0 0 1 19.2 11.85V20.05A1.15 1.15 0 0 1 18.05 21.2H7.7A1.15 1.15 0 0 1 6.55 20.05V11.85A1.15 1.15 0 0 1 7.7 10.7Z"
        fill={`url(#${backId})`}
        opacity="0.9"
      />
      <path d={page} fill={`url(#${fillId})`} />
      <path d={page} fill={`url(#${sheenId})`} />
      <g clipPath={`url(#${clipId})`}>
        <path
          d={page}
          fill="none"
          stroke="#fff"
          strokeOpacity="0.18"
          strokeWidth="1.05"
          strokeLinejoin="round"
        />
      </g>
      <path d={fold} fill={`url(#${foldId})`} />
      <path
        d={`M${foldX} ${t}L${r} ${foldY}`}
        stroke="#fff"
        strokeOpacity="0.22"
        strokeWidth="0.5"
        strokeLinecap="round"
      />

      {showLines && (
        <g
          clipPath={`url(#${clipId})`}
          stroke="#fff"
          strokeWidth={hair}
          strokeLinecap="round"
          opacity="0.36"
        >
          <path d={`M${lineX} ${lineY0}h${lineW}`} />
          <path d={`M${lineX} ${lineY0 + lineGap}h${lineW}`} />
          <path d={`M${lineX} ${lineY0 + lineGap * 2}h${lineW}`} />
          <path d={`M${lineX} ${lineY0 + lineGap * 3}h${lineW * 0.64}`} />
        </g>
      )}
    </svg>
  );
};

/** Loose docs — the DocIcon fanned like cards in a hand. */
export const UnfiledIcon = ({ size = 14, className }) => {
  const page = Math.round(size * 0.78);
  const inset = (size - page) / 2;
  const cards = [
    { id: 'left', rotate: -16, brightness: 0.72 },
    { id: 'right', rotate: 16, brightness: 0.72 },
    { id: 'front', rotate: 0, brightness: 1 },
  ];
  return (
    <span
      className={className}
      aria-hidden
      style={{
        position: 'relative',
        display: 'block',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {cards.map(({ id, rotate, brightness }) => (
        <span
          key={id}
          style={{
            position: 'absolute',
            left: inset,
            bottom: inset * 0.35,
            width: page,
            height: page,
            transform: `rotate(${rotate}deg)`,
            transformOrigin: '50% 100%',
            filter: brightness < 1 ? `brightness(${brightness})` : undefined,
          }}
        >
          <DocIcon size={page} />
        </span>
      ))}
    </span>
  );
};
